<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-3 hover:shadow-xl transition-shadow duration-300">
    <!-- Header with Animation -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center space-x-2">
        <div class="p-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg animate-pulse">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
        </div>
        <h4 class="text-xs font-semibold text-gray-900 dark:text-white">
          Variation {{ currentIndex + 1 }}/{{ totalVariations }}
        </h4>
      </div>
      
      <!-- Navigation Controls with Hover Effects -->
      <div class="flex items-center space-x-1">
        <button
          @click="$emit('previous')"
          :disabled="currentIndex === 0"
          class="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-110 active:scale-95 group"
          :class="{ 'animate-pulse': currentIndex === 0 }"
        >
          <svg class="w-3 h-3 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <button
          @click="$emit('next')"
          :disabled="currentIndex === totalVariations - 1"
          class="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-110 active:scale-95 group"
          :class="{ 'animate-pulse': currentIndex === totalVariations - 1 }"
        >
          <svg class="w-3 h-3 transform group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
    
    <!-- Thumbnail Gallery -->
    <div class="flex items-center justify-center space-x-2 mb-3">
      <div
        v-for="(variation, index) in variations"
        :key="index"
        @click="$emit('select-variation', index)"
        :class="[
          'flex-shrink-0 rounded-lg transition-all duration-300 cursor-pointer relative overflow-hidden group border-2',
          currentIndex === index 
            ? 'border-blue-500 shadow-lg scale-105' 
            : 'border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:scale-110'
        ]"
      >
        <!-- Thumbnail Image -->
        <div class="w-12 h-16 bg-gray-100 dark:bg-gray-700 rounded-md overflow-hidden">
          <img 
            :src="variation.thumbnailUrl || variation.imageUrl" 
            :alt="`Variation ${index + 1}`"
            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            @error="handleImageError"
          />
        </div>
        
        <!-- Active Indicator -->
        <div v-if="currentIndex === index" 
             class="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-md">
          <div class="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
        </div>
        
        <!-- Hover Overlay -->
        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-md flex items-center justify-center">
          <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <svg class="w-4 h-4 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
            </svg>
          </div>
        </div>
        
        <!-- Tooltip on Hover -->
        <div class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
          Variation {{ index + 1 }}
        </div>
      </div>
    </div>

    <!-- Interactive Progress Dots -->
    <div class="flex items-center justify-center space-x-2 mb-2">
      <div
        v-for="(_, index) in totalVariations"
        :key="index"
        @click="$emit('select-variation', index)"
        :class="[
          'rounded-full transition-all duration-300 cursor-pointer relative overflow-hidden group',
          currentIndex === index 
            ? 'w-6 h-2 bg-gradient-to-r from-blue-500 to-purple-500 shadow-lg' 
            : 'w-2 h-2 bg-gray-300 dark:bg-gray-600 hover:bg-blue-400 dark:hover:bg-blue-500 hover:scale-125'
        ]"
      >
        <!-- Active Dot Animation -->
        <div v-if="currentIndex === index" 
             class="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse"></div>
        
        <!-- Hover Ripple Effect -->
        <div class="absolute inset-0 bg-white opacity-0 group-hover:opacity-30 group-active:opacity-50 transition-opacity duration-200 rounded-full"></div>
        
        <!-- Tooltip on Hover -->
        <div class="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
          Variation {{ index + 1 }}
        </div>
      </div>
    </div>
    
    <!-- Keyboard Shortcuts Hint -->
    <div class="text-center">
      <div class="inline-flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-full">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
        <span>Use ← → keys or swipe</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Variation {
  id: string
  imageUrl: string
  thumbnailUrl?: string
  dimensions: {
    width: number
    height: number
  }
  validationScore?: number
  isPrimary: boolean
}

interface Props {
  currentIndex: number
  totalVariations: number
  variations: Variation[]
}

defineProps<Props>()

defineEmits<{
  'previous': []
  'next': []
  'select-variation': [index: number]
}>()

// Handle image loading errors
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  // Fallback to a placeholder
  img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xNiAxNkgyNFYyNEgxNlYxNloiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHBhdGggZD0iTTIwIDIwTDIyIDIyTDI0IDIwTDI2IDIyVjI4SDE4VjIyTDIwIDIwWiIgc3Ryb2tlPSIjOUNBM0FGIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K'
}
</script>

<style scoped>
/* Custom animations for smooth interactions */
@keyframes ripple {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(4);
    opacity: 0;
  }
}

.group:active::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle, rgba(255,255,255,0.5) 0%, transparent 70%);
  animation: ripple 0.6s ease-out;
  pointer-events: none;
}
</style>